import { Box, Typography } from '@mui/material';
import { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAppDispatch, useAppSelector } from '../../../../../app/redux/hooks';
import constants from '../../../../../utils/constants';
import CustomRadioButton from '../../../../../common/components/customized/customradiobutton/CustomRadioButton';
import CustomTextField from '../../../../../common/components/customized/customtextfield/CustomTextField';
import GoogleSearchBox from '../../../../../common/components/maps/googlesearchbox/GoogleSearchBox';
import CustomButton from '../../../../../common/components/buttons/CustomButton';
import CustomMultiSelect from '../../../../../common/components/customized/custommultiselect/CustomMultiSelect';
import { useDynamicYupSchema } from '../../../../../common/hooks/useDynamicSchema';
import { GeofenceAssignVehicle, GeofenceData } from '../hooks/geofenceDatas';
import './AddGeofence.scss';
import {
  useGeofenceLocationFunc,
  useGeofenceRadioFunc,
  useGeofenceRadiusFunc
} from '../hooks/geofenceTextFieldFunctions';
import { useAddGeofenceAssign } from '../hooks/geofenceAssignVehicle';
import { useGeofenceFormDispatch } from '../hooks/geofenceFormSubmission';
import { usegetGeofences } from '../hooks/getGeofences';

const AddGeofence = ({
  setIsOpen,
  setAddress,
  address,
  filteredRow,
  setFilteredRow,
  isOpen,
  setMapClick,
  isUpdate,
  setIsUpdate,
  isCircular,
  setIsCircular,
  setToggleLock,
  setSearchValue
}: any) => {
  const { data: deviceList } = useAppSelector(state => state.tripsDeviceList || []);
  const { fences } = useAppSelector(state => state.circle);
  const { data, isLoading, type } = useAppSelector(state => state?.geofences);
  const { polygonFences } = useAppSelector(state => state.polygon);
  const abortController = useRef<AbortController | null>(null);

  const { createFormFields } = useDynamicYupSchema(null);
  const { schema } = createFormFields(GeofenceAssignVehicle());

  const dispatch = useAppDispatch();

  const {
    handleSubmit,
    setValue,
    control,
    getValues,
    setError,
    clearErrors,
    reset,
    resetField,
    trigger
  } = useForm({
    resolver: async (data: any, context: any, options: any) => {
      const { schema }: any = createFormFields(GeofenceData(data));
      return yupResolver(schema)(data, context, options);
    }
  });

  const {
    control: assignControl,
    handleSubmit: assignVehicleSubmit,
    reset: formReset,
    setValue: assignVehicleValue
  } = useForm({
    resolver: yupResolver(schema)
  });

  const { handleGeoTypeChange } = useGeofenceRadioFunc({
    setValue,
    getValues,
    clearErrors,
    setIsCircular,
    setAddress,
    polygonFences,
    fences,
    dispatch
  });

  const { handleLocationChange } = useGeofenceLocationFunc({
    resetField,
    setAddress,
    dispatch,
    filteredRow,
    fences
  });

  const { handleRadiusChange } = useGeofenceRadiusFunc({
    setValue,
    trigger,
    getValues,
    setError,
    filteredRow,
    fences,
    dispatch
  });

  const { getGeofences } = usegetGeofences({ data });
  const { onSubmit, onClose } = useGeofenceFormDispatch({
    address,
    clearErrors,
    filteredRow,
    setValue,
    fences,
    dispatch,
    isCircular,
    polygonFences,
    isOpen,
    setAddress,
    data,
    getGeofences,
    setIsCircular,
    setIsUpdate,
    abortController,
    isUpdate,
    setSearchValue,
    setIsOpen,
    setFilteredRow,
    setToggleLock,
    reset
  });

  const { mappedDeviceList, assignGeofence, mappedVehicles } = useAddGeofenceAssign({
    deviceList,
    filteredRow,
    setIsOpen,
    dispatch,
    abortController,
    setSearchValue,
    formReset,
    onClose,
    isOpen,
    assignVehicleValue,
    getValues,
    setMapClick
  });

  return (
    <>
      {(isOpen === 'Add Geofence' || isOpen === 'Update Geofence') && (
        <Box
          component='form'
          onSubmit={handleSubmit(onSubmit)}
          className='animate__animated animate__slideInRight animate__faster add-geofence-container'
        >
          <Typography className='custom-drawer-typo1'>
            {filteredRow ? constants?.UpdateGeofence : constants.Geofence}
          </Typography>
          <CustomRadioButton
            name='geoType'
            control={control}
            formlabel='Select Geofence Type'
            radiobutton1='Circular'
            radiobutton2='Polygon'
            value1='circular'
            value2='polygon'
            value={filteredRow?.geoType}
            disabled={isUpdate}
            onChangeCallback={e => handleGeoTypeChange(e)}
          />

          {GeofenceData()?.map((item: any, index: number) => (
            <>
              {item?.name === 'geoZoneName' && (
                <CustomTextField
                  id={item?.id}
                  control={control}
                  name={item?.name}
                  label={item?.label}
                  placeholder={item?.placeholder}
                />
              )}
              {isCircular && (
                <>
                  {item?.name === 'location' && (
                    <GoogleSearchBox
                      id={item?.id}
                      name={item?.name}
                      control={control}
                      label={item?.label}
                      placeholder={item?.placeholder}
                      value={address?.formatted_address}
                      setAddress={setAddress}
                      onchange={(e: any) => handleLocationChange(e)}
                    />
                  )}

                  {item?.name === 'radius' && (
                    <CustomTextField
                      id={item?.id}
                      control={control}
                      name={item?.name}
                      label={item?.label}
                      placeholder={item?.placeholder}
                      type='number'
                      defaultValue={filteredRow ? filteredRow?.radius : ''}
                      inputProps={{
                        maxLength: 4,
                        step: '0.01',
                        min: '0.1'
                      }}
                      onChangeCallback={(e: string) => handleRadiusChange(e)}
                    />
                  )}
                </>
              )}
            </>
          ))}
          <Box className='geofence-cancel'>
            <CustomButton
              loading={
                isLoading && (type === 'save-geofence' || type === 'update-geofence')
              }
              category={isUpdate ? constants?.Update : constants?.Add}
              className='saveChanges'
              type='submit'
            />
            <CustomButton className='cancel' category='Cancel' onClick={onClose} />
          </Box>
        </Box>
      )}
      {isOpen === 'Assign Geofence' && (
        <Box className='animate__animated animate__slideInRight animate__faster geofence-assign-container'>
          <Typography className='mapping-title-geofence'>
            {constants?.GeofenceMapping}
          </Typography>
          <Box
            className='assign-add'
            component={'form'}
            onSubmit={assignVehicleSubmit(assignGeofence)}
          >
            {GeofenceAssignVehicle()?.map((item: any, index: number) => (
              <>
                {item?.name === 'geofence' ? (
                  <CustomTextField
                    id={item?.id}
                    name={item?.name}
                    label={item?.label}
                    control={assignControl}
                    disabled={true}
                    placeholder={item?.placeholder}
                  />
                ) : (
                  <CustomMultiSelect
                    id={item?.id}
                    name={item?.name}
                    placeholder={item?.placeholder}
                    control={assignControl}
                    type={'loads'}
                    width={'200px'}
                    isOptional
                    setValue={assignVehicleValue}
                    label={item?.placeholder}
                    options={mappedDeviceList}
                    initialValue={
                      filteredRow?.vehicleNumber ? filteredRow?.vehicleNumber : []
                    }
                    mappedList={mappedVehicles}
                  />
                )}
              </>
            ))}
            <Box className='geofencemapping-cancel'>
              <CustomButton
                category='Submit'
                className='saveChanges'
                type='submit'
                loading={type === 'geofence-mapping' && isLoading}
              />
              <CustomButton
                category='Cancel'
                className='cancel'
                onClick={() => {
                  formReset();
                  setIsOpen('');
                  onClose();
                }}
              />
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default AddGeofence;
